import { useState, useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

interface EditThreadModalProps {
  threadId: string
  currentTitle: string
  currentBody: string
  onClose: () => void
  onSuccess: () => void
}

export default function EditThreadModal({
  threadId,
  currentTitle,
  currentBody,
  onClose,
  onSuccess,
}: EditThreadModalProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [threadTitle, setThreadTitle] = useState(currentTitle)
  const [threadBody, setThreadBody] = useState(currentBody)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const titleInputRef = useRef<HTMLInputElement>(null)

  // Update form when props change
  useEffect(() => {
    setThreadTitle(currentTitle)
    setThreadBody(currentBody)
  }, [currentTitle, currentBody])

  // Focus first input on mount
  useEffect(() => {
    titleInputRef.current?.focus()
  }, [])

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose, isSubmitting])

  const updateThread = useMutation({
    mutationFn: async (data: { title: string; body: string }) => {
      if (!user) throw new Error('Not authenticated')

      const { error: threadError } = await supabase
        .from('threads')
        .update({
          title: data.title.trim(),
          body: data.body.trim(),
        })
        .eq('id', threadId)

      if (threadError) throw threadError
    },
    onSuccess: () => {
      // Invalidate threads queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['group-threads'] })
      queryClient.invalidateQueries({ queryKey: ['thread', threadId] })
      queryClient.invalidateQueries({ queryKey: ['threads-from-my-groups'] })
      queryClient.invalidateQueries({ queryKey: ['latest-threads'] })
      
      onSuccess()
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Basic validation
    if (!threadTitle.trim()) {
      setError('Thread title is required')
      return
    }

    if (threadTitle.trim().length < 3) {
      setError('Thread title must be at least 3 characters')
      return
    }

    if (!threadBody.trim()) {
      setError('Thread content is required')
      return
    }

    if (threadBody.trim().length < 10) {
      setError('Thread content must be at least 10 characters')
      return
    }

    setIsSubmitting(true)
    try {
      await updateThread.mutateAsync({
        title: threadTitle.trim(),
        body: threadBody.trim(),
      })
    } catch (err: any) {
      setError(err.message || 'Failed to update thread')
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-thread-title"
    >
      <div
        className="rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '2px solid var(--bg-tertiary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 
            id="edit-thread-title"
            className="text-2xl font-bold"
            style={{ color: 'var(--text-primary)' }}
          >
            Edit Thread
          </h2>
          <button
            onClick={onClose}
            className="p-2"
            aria-label="Close dialog"
            style={{ color: 'var(--text-primary)' }}
            disabled={isSubmitting}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Thread Title Field */}
          <div>
            <label 
              htmlFor="edit-thread-title-input"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Thread Title
              <span style={{ color: 'var(--color-error)' }} aria-label="required"> *</span>
            </label>
            <input
              ref={titleInputRef}
              type="text"
              id="edit-thread-title-input"
              value={threadTitle}
              onChange={(e) => setThreadTitle(e.target.value)}
              placeholder="Enter thread title"
              required
              minLength={3}
              maxLength={200}
              className="w-full px-4 py-2 border-2 rounded"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
              aria-required="true"
              disabled={isSubmitting}
            />
          </div>

          {/* Thread Body Field */}
          <div>
            <label 
              htmlFor="edit-thread-body-input"
              className="block mb-2 font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Thread Content
              <span style={{ color: 'var(--color-error)' }} aria-label="required"> *</span>
            </label>
            <textarea
              id="edit-thread-body-input"
              value={threadBody}
              onChange={(e) => setThreadBody(e.target.value)}
              placeholder="Write your thread content here..."
              rows={8}
              required
              minLength={10}
              className="w-full px-4 py-2 border-2 rounded"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
              aria-required="true"
              disabled={isSubmitting}
            />
            <p 
              className="text-sm mt-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              Minimum 10 characters required
            </p>
          </div>

          {error && (
            <div 
              role="alert"
              className="p-3 rounded"
              style={{
                backgroundColor: 'var(--color-error)',
                color: 'var(--text-inverse)',
              }}
            >
              {error}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 font-semibold rounded disabled:opacity-50"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
              }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 font-semibold rounded disabled:opacity-50"
              style={{
                backgroundColor: 'var(--bg-interactive)',
                color: 'var(--text-inverse)',
              }}
            >
              {isSubmitting ? 'Updating...' : 'Update Thread'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

